import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Check, ArrowRight, Sparkles } from 'lucide-react';

const PricingSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <section 
      id="pricing" 
      ref={ref}
      className="section-spacing relative bg-white"
      data-testid="pricing-section"
    >
      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.pricing.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
            {t.pricing.title}
          </h2>
        </motion.div>
        
        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-md mx-auto"
        >
          <div className="card-light rounded-3xl p-8 border-[#2563EB]/20 relative overflow-hidden shadow-xl shadow-[#2563EB]/10">
            {/* Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#2563EB]/10 blur-[100px]" />
            
            <div className="relative z-10">
              {/* Trial Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700 font-medium">{t.pricing.trial}</span>
              </div>
              
              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl lg:text-6xl font-bold text-[#0F172A]">{t.pricing.price}</span>
                  <span className="text-xl text-[#475569]">{t.pricing.currency}</span>
                  <span className="text-[#94A3B8]">{t.pricing.period}</span>
                </div>
              </div>
              
              {/* Features */}
              <div className="space-y-4 mb-8">
                {t.pricing.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#2563EB]" />
                    </div>
                    <span className="text-[#475569]">{feature}</span>
                  </motion.div>
                ))}
              </div>
              
              {/* CTA */}
              <a
                href="https://dashboard.aqera.app"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center group"
                data-testid="pricing-cta"
              >
                {t.pricing.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
